
import React from "react";

import { DateTime, Duration, Interval } from "luxon";

import * as d3 from 'd3';

import { makeColorBar } from "autumnplot-gl";

import logo from '../img/TA-Logo-TS-banner_copy.png';

import svgToCanvas from "../utils/svgToCanvas";
import { SelectionInterval } from "../utils/SelectionInterval";
import TornadoCollection from "../utils/TorCollection";
import { Filter, PartitionFilter, PropertyFilter, UnitSystem } from "../utils/Filter";
import {EnvironmentDataInterface, EnvironmentSource, LayerType} from "../utils/EnvironmentDataHandler";
import { ATTRIBUTION_LINKS, AttributionSource } from "./Constants";

import './MapOverlayWidget.css';

interface MapOverlayWidgetProps {
    controls_visible: boolean
    interval: SelectionInterval;
    tor_coll: TornadoCollection | null;
    filters: Record<string, Filter>;
    data_limits: Interval;

    sel_data_time: DateTime | null;
    env_handler: EnvironmentDataInterface | null;
    env_type: LayerType | null;
    env_src: EnvironmentSource | null;
    temp_display: 'All' | 'Hourly' | 'None';
    cday_offset: Duration;
    units: UnitSystem;
    attribution: (AttributionSource)[];

    window_size: {width: number, height: number}

    setscreenshotfunc: (ss_func: () => Promise<HTMLCanvasElement>, which: string) => void;
}

class MapOverlayWidget extends React.PureComponent<MapOverlayWidgetProps> {
    injuries: number;
    fatalities: number;
    injuries_atleast: boolean;
    fatalities_atleast: boolean;
    ntors: number;

    overlay: HTMLDivElement | null;
    ntor_el: HTMLDivElement | null;
    ninj_el: HTMLDivElement | null;
    nfat_el: HTMLDivElement | null;
    attribution: HTMLDivElement | null;

    constructor(props: MapOverlayWidgetProps) {
        super(props);

        this.injuries = 0;
        this.fatalities = 0;
        this.injuries_atleast = false;
        this.fatalities_atleast = false;
        this.ntors = 0;

        this.overlay = null;
        this.ntor_el = null;
        this.ninj_el = null;
        this.nfat_el = null;
        this.attribution = null;
    }

    createColorbar() {
        if (this.overlay === null) return; // Not sure why this happens

        const font_name = 'Trebuchet MS';

        let root = d3.select(this.overlay);
        root.selectAll('#colorbar').remove();

        const colormap_stuff = EnvironmentDataInterface.getColormaps(this.props['env_type']);

        if (colormap_stuff === null || this.props['sel_data_time'] === null) {
            return;
        }

        const {colormap, colorbar_info} = colormap_stuff;

        const colorbar = makeColorBar(colormap, {'fontface': font_name, 'tick_direction': 'left', 'label': colorbar_info.label, 'ticks': colorbar_info.ticks, 'ticklabelsize': 9});
        root.append('div')
            .attr('id', 'colorbar')
            .attr('class', this.props['controls_visible'] ? '' : 'controls-hidden')
            .node()?.appendChild(colorbar);
    }

    windowResize() {
        this.createColorbar();
        this.updateStatistics();
    }

    componentDidMount() {
        this.props['setscreenshotfunc'](this.screenshot.bind(this), 'overlay');
        this.createColorbar();

        window.addEventListener('resize', this.windowResize.bind(this));
        this.updateStatistics();

        this.updateAttribution();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.windowResize.bind(this));
    }

    componentDidUpdate(prev_props: MapOverlayWidgetProps) {
        if (this.props['controls_visible'] != prev_props['controls_visible']) {
            this.createColorbar();
        }

        if (this.props['env_type'] != prev_props['env_type'] 
            || this.props['sel_data_time'] === null || prev_props['sel_data_time'] === null) {
            this.createColorbar();
        }

        //console.log(this.props['tor_coll'], prev_props['tor_coll']);
        if (this.props['tor_coll'] !== prev_props['tor_coll'] || this.props['interval'] !== prev_props['interval'] 
            || this.props['temp_display'] !== prev_props['temp_display']) {
            this.updateStatistics();
        }

        this.updateAttribution();
    }

    async updateStatistics() {
        if (this.ntor_el === null || this.ninj_el === null || this.nfat_el === null) {
            return;
        }

        const qc_injfat =  (v: string | number) => v !== undefined && Number.isInteger(v) && v >= 0;

        if (this.props['temp_display'] == 'None') {
            this.ntor_el.innerHTML = "";
            this.ninj_el.innerHTML = "";
            this.nfat_el.innerHTML = "";
            return;
        }

        if (this.props['tor_coll'] !== null) {
            this.ntors = (await this.props['tor_coll'].getNTornadoes([this.props['interval']], undefined, true))[0]['count'];
            this.injuries = 0;
            this.fatalities = 0;

            if (this.ntors > 0) {
                const injuries_prom = this.props['tor_coll'].getPropertyValues('injuries', this.props['interval']);
                const fatalities_prom = this.props['tor_coll'].getPropertyValues('fatalities', this.props['interval']);

                const injuries = (await injuries_prom)['injuries'] as number[];
                const fatalities = (await fatalities_prom)['fatalities'] as number[];

                this.injuries_atleast = injuries.filter(v => !qc_injfat(v)).length > 0;
                this.fatalities_atleast = fatalities.filter(v => !qc_injfat(v)).length > 0;

                this.injuries = injuries.filter(qc_injfat).reduce((a, b) => a + b, 0);
                this.fatalities = fatalities.filter(qc_injfat).reduce((a, b) => a + b, 0);
            }

            let num_header: string, inj_header: string, fat_header: string;
            if (this.props['window_size']['width'] >= 700) {
                num_header = "Number of Tornadoes: ";
                inj_header = "Injuries: ";
                fat_header = "Fatalities: ";
            }
            else {
                num_header = "Num: ";
                inj_header = "Inj: ";
                fat_header = "Fat: ";
            }
            
            if (this.ntor_el !== null && this.ninj_el !== null && this.nfat_el !== null) {
                this.ntor_el.innerHTML = num_header + this.ntors;
                this.ninj_el.innerHTML = inj_header + (this.injuries_atleast ? '\u2265' : '') + this.injuries;
                this.nfat_el.innerHTML = fat_header + (this.fatalities_atleast ? '\u2265' : '') + this.fatalities;
            }
        }
    }

    updateAttribution() {
        if (this.attribution === null) {
            return;
        }

        const add_copy =  (attr: string) => (attr != 'Sources' ? '&copy; ' : '') + attr;
        const attribution_str = 'Data: ' 
            + this.props['attribution'].map(attr => '<a href="' + ATTRIBUTION_LINKS[attr] + '" target="_blank">' + add_copy(attr) + '</a>')
                                       .join(' | ');

        this.attribution.innerHTML = attribution_str;
    }

    screenshot() {
        const need_colorbar = this.props['sel_data_time'] !== null && this.props['env_type'] !== null;
        const ta_logo_promise: Promise<HTMLImageElement> = new Promise((resolve, reject) => {
            let img = new Image();
            img.onload = () => resolve(img);
            img.src = logo;
        });
    
        let promises = [ta_logo_promise];

        if (need_colorbar) {
            const colorbar_promise: Promise<HTMLImageElement> = new Promise((resolve, reject) => {
                if (this.overlay === null) {
                    reject("Overlay is unexpectedly null in map overlay screenshot function");
                    return;
                }

                const dpr = window.devicePixelRatio || 1;

                const svg = this.overlay.querySelector('svg');
                if (svg === null) {
                    reject('svg element is missing in map overlay screenshot function');
                    return;
                }

                const cbar_elem = document.querySelector('#colorbar');
                if (cbar_elem === null) {
                    reject("#colorbar is missing in map overlay screenshot");
                    return;
                }

                const bar_bg_rect = cbar_elem.getBoundingClientRect();
                const cbar_style = window.getComputedStyle(cbar_elem);
                const cbar_border_radius_str = cbar_style.borderBottomRightRadius;
                const cbar_background_color = cbar_style.backgroundColor;

                let canvas = document.createElement('canvas');
                canvas.width = bar_bg_rect['width'] * dpr;
                canvas.height = bar_bg_rect['height'] * dpr;

                const roundRect = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
                    if (w < 2 * r) r = w / 2;
                    if (h < 2 * r) r = h / 2;

                    ctx.beginPath();
                    ctx.moveTo(x+r, y);
                    ctx.arcTo(x+w, y,   x+w, y+h, r);
                    ctx.arcTo(x+w, y+h, x,   y+h, r);
                    ctx.arcTo(x,   y+h, x,   y,   r);
                    ctx.arcTo(x,   y,   x+w, y,   r);
                    ctx.closePath();
                }

                const cbar_border_radius = parseInt(cbar_border_radius_str.includes('px') ? cbar_border_radius_str.slice(0, -2): cbar_border_radius_str); 

                let ctx = canvas.getContext('2d');
                if (ctx === null) {
                    throw "Could not get a drawing context in the map overlay colorbar drawing function";
                }

                roundRect(ctx, -cbar_border_radius * dpr, 0, 
                    (bar_bg_rect['width'] + cbar_border_radius) * dpr, bar_bg_rect['height'] * dpr, cbar_border_radius * dpr);

                ctx.fillStyle = cbar_background_color;
                ctx.fill();

                svgToCanvas(svg, canvas, 0, 0, dpr);

                let img = new Image();
                img.onload = () => resolve(img);
                img.src = canvas.toDataURL();
            });

            promises.push(colorbar_promise)
        }

        return Promise.all(promises).then(results => {
            const logo_img = results[0];
            const colorbar_img = need_colorbar ? results[1]: null;

            const dpr = window.devicePixelRatio || 1;
            const rect = document.getElementsByClassName('overlay')[0].getBoundingClientRect();

            let canvas = document.createElement('canvas');
            canvas.width = rect.width * dpr;
            canvas.height = rect.height * dpr;

            let ctx = canvas.getContext('2d');
            if (ctx === null) {
                throw "Could not get a drawing context in the map overlay screenshot function";
            }

            // This is gonna get stoopid
            const header = document.getElementById('map-header');
            if (header === null) {
                throw "#map-header element is missing";
            }

            const header_rect = header.getBoundingClientRect();
            const header_style = window.getComputedStyle(header);
            const background_color = header_style['backgroundColor'];

            // Draw header background
            ctx.beginPath();
            ctx.fillStyle = background_color;
            ctx.rect(header_rect['x'] * dpr, header_rect['y'] * dpr, header_rect['width'] * dpr, header_rect['height'] * dpr);
            ctx.fill();

            // Create statistics text
            ctx.fillStyle = '#000000';
            ctx.font = (16 * 0.9 * dpr) + 'px Trebuchet MS';
            ctx.textAlign = 'right';
            ctx.textBaseline = 'top';

            const fat_str = (this.fatalities_atleast ? '\u2265' : '') + this.fatalities
            const inj_str = (this.injuries_atleast ? '\u2265' : '') + this.injuries

            if (this.props['temp_display'] != 'None') {
                ctx.fillText('Number of Tornadoes: ' + this.ntors, (header_rect['width'] - 8) * dpr, 2 * dpr);
                ctx.fillText('Fatalities: ' + fat_str + '    Injuries: ' + inj_str, (header_rect['width'] - 8) * dpr, 21 * dpr);
            }

            // Draw TA logo
            const logo_width = logo_img.width;
            const logo_height = logo_img.height;
            const logo_aspect = logo_width / logo_height;
            const logo_draw_height = 41;
            const logo_y = (header_rect['height'] - logo_draw_height) / 2;

            ctx.drawImage(logo_img, 0, logo_y * dpr, logo_draw_height * dpr * logo_aspect, logo_draw_height * dpr);

            // Create date and filter header text
            const [date_header_str, filter_str] = this._createMapHeaders();
            let env_data_str = "";
            if (need_colorbar && this.props['env_type'] !== null && this.props['sel_data_time'] !== null && this.props['env_handler'] !== null) {
                env_data_str = this.props['env_handler'].getTitle(this.props['env_type']) + " Valid " 
                    + this.props['sel_data_time'].toFormat("HHmm 'UTC' MMM d, yyyy");
            }

            ctx.textAlign = 'left';
            let y = 2 * dpr;

            if (date_header_str != "") {
                ctx.fillText(date_header_str, 140 * dpr, y);
                y += 17 * dpr;
            }

            if (filter_str != "") {
                ctx.fillText(filter_str, 140 * dpr, y);
                y += 17 * dpr;
            }

            ctx.fillText(env_data_str, 140 * dpr, y);

            if (colorbar_img !== null) {
                const colorbar_height = colorbar_img.height;
                const colorbar_width = colorbar_img.width;
                const colorbar_y = canvas.height / 2 - colorbar_height / 2;
                ctx.drawImage(colorbar_img, 0, colorbar_y, colorbar_width, colorbar_height);
            }

            const fontsize = 14;
            const height = fontsize + 6;

            if (this.props['attribution'].includes('ESWD')) {
                let data_text = 'Data: European Severe Weather Database'; 
                const data_width = (data_text.length + 3) / fontsize * 95;
    
                ctx.fillStyle = 'rgba(255,255,255,0.4)';
                ctx.fillRect(canvas.width - data_width * dpr, canvas.height - 2 * height * dpr, data_width * dpr, height * dpr);

                ctx.fillStyle = 'black';
                ctx.textAlign = 'right';
                ctx.textBaseline = 'bottom';
                ctx.fillText(data_text, canvas.width - 3 * dpr, canvas.height + (-height - 3) * dpr);
            }

            let text = '© MapTiler © OpenStreetMap contributors';
            if (canvas.width < 300) text = '© OSM';
            const width = (text.length + 3) / fontsize * 100;

            ctx.fillStyle = 'rgba(255,255,255,0.4)';
            ctx.fillRect(canvas.width - width * dpr, canvas.height - height * dpr, width * dpr, height * dpr);

            ctx.font = (fontsize * dpr) + 'px Arial';
            ctx.fillStyle = 'black';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'bottom';
            ctx.fillText(text, canvas.width - (width - 3) * dpr, canvas.height - 3 * dpr);

            return canvas;
        });
    }

    _createMapHeaders() {
        if (this.props['temp_display'] == 'None') {
            return ['', ''];
        }
        
        const filters = Object.values(this.props['filters']).filter((f: Filter) : f is PropertyFilter => f.display(this.props['units']) !== null);
        const filter_str = filters.map(filt => filt.toHeaderString(this.props['units'])).join(', ');
        let partition_filter_str = (this.props['filters']['partition'] as PartitionFilter).toHeaderString();
        partition_filter_str = partition_filter_str == "" ? partition_filter_str : partition_filter_str + " ";
        
        const data_limits = Interval.fromDateTimes(this.props['data_limits'].start.plus(this.props['cday_offset']), 
                                                   this.props['data_limits'].end.plus(this.props['cday_offset']));

        const intv_string_parts = this.props['interval'].toString(this.props['cday_offset'], data_limits, true);

        return [partition_filter_str + 'Tornadoes ' + intv_string_parts['preposition'] + ' ' + intv_string_parts['string'], filter_str];
    }

    render() {
        const short_cutoff = 700;
        const [date_header_str, filter_str] = this._createMapHeaders();

        let env_data_str = "";

        if (this.props['env_type'] !== null && this.props['sel_data_time'] !== null && this.props['env_handler'] !== null) {
            const which_title = this.props['window_size']['width'] > short_cutoff ? 'normal' : 'short';
            const data_title = this.props['env_handler'].getTitle(this.props['env_type'], which_title);
            const valid_str = this.props['window_size']['width'] > short_cutoff ? ' Valid ' : ' @ ';
            const valid_fmt = this.props['window_size']['width'] > short_cutoff + 200 ? "HHmm 'UTC' MMM d, yyyy" : "HH'Z' MMM d";
            env_data_str = data_title + valid_str 
                + this.props['sel_data_time'].toFormat(valid_fmt);
        }

        const date_header_div = date_header_str === "" ? "": <div>{date_header_str}</div>
        const filter_div = filter_str === "" ? "": <div>{filter_str}</div>

        const visible_class = this.props['controls_visible'] ? '' : 'controls-hidden'
        const small_screen_class = this.props['window_size']['width'] > short_cutoff + 100 ? "" : "small-screen";

        const font_ems = Math.min(0.9, this.props['window_size']['width'] / 550);

        return (
            <div className={'overlay ' + visible_class} 
                ref={el => this.overlay = el}
                >
                <div id='map-header' style={{'fontSize': font_ems + 'rem'}}>
                    <div id='logo'><a href='https://tornadoarchive.com/home' target='_blank'><img src={logo}></img></a></div>
                    <div id='date-header'>
                        {date_header_div}
                        {filter_div}
                        <div>{env_data_str}</div>
                    </div>
                    <div id='statistics-header' className={small_screen_class}>
                        <div>
                            <div ref={el => this.ntor_el = el}></div>
                        </div>
                        <div>
                            <div ref={el => this.ninj_el = el}></div>
                            <div ref={el => this.nfat_el = el}></div>
                        </div>
                    </div>
                </div>
                <div id='attribution' className={visible_class + " " + small_screen_class} ref={el => this.attribution = el}></div>
            </div>
        );
    }
}

export default MapOverlayWidget;